// Farm operations use Philippine calendar dates.  Timestamps remain absolute
// values; these helpers are only for selecting, displaying, and querying a
// farm-calendar day.
export const FARM_TIMEZONE = "Asia/Manila";

const farmPartsFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: FARM_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function validDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function farmCalendarParts(value = new Date()) {
  const date = validDate(value);
  if (!date) return null;
  const parts = Object.fromEntries(
    farmPartsFormatter.formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
  };
}

export function farmDateIso(value = new Date()) {
  const parts = farmCalendarParts(value);
  if (!parts) return "";
  return `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
}

export function parseFarmCalendarValue(value) {
  const text = String(value || "");
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function farmDaysInMonth(year, month) {
  return new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate();
}

function shiftFarmDate(isoDate, days) {
  const [year, month, day] = String(isoDate).split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days, 12));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, "0")}-${String(shifted.getUTCDate()).padStart(2, "0")}`;
}

export function farmDateRangeForPreset(preset, value = new Date()) {
  const today = farmDateIso(value);
  if (!today) return { from: "", to: "" };
  if (preset === "today") return { from: today, to: today };
  if (preset === "yesterday") {
    const yesterday = shiftFarmDate(today, -1);
    return { from: yesterday, to: yesterday };
  }
  if (preset === "week") {
    const [year, month, day] = today.split("-").map(Number);
    const weekday = new Date(Date.UTC(year, month - 1, day, 12)).getUTCDay();
    return { from: shiftFarmDate(today, weekday === 0 ? -6 : 1 - weekday), to: today };
  }
  if (preset === "month") {
    const { year, month } = farmCalendarParts(value);
    return { from: `${year}-${String(month).padStart(2, "0")}-01`, to: today };
  }
  return { from: today, to: today };
}

export function formatFarmCalendarDate(value = new Date(), options = {}) {
  const date = validDate(value);
  if (!date) return "";
  return date.toLocaleDateString(undefined, { timeZone: FARM_TIMEZONE, ...options });
}
